import React from 'react'
import styles from './styles.css'
import {
  DurationSelectorComponent,
  DurationSelectorFragment,
  SellableUrlFragment
} from '#src/generated/graphql'
import { Select, FullBlockLoader, CircleLoader } from '@deal/components'
import { formatSellablePricing } from '#src/services/sellable'

interface DurationPriceProps {
  sellable: DurationSelectorFragment
}

const DurationPrice: React.SFC<DurationPriceProps> = ({ sellable }) => {
  const formattedPricing = formatSellablePricing(sellable)
  return (
    <div className={styles.durationPrice}>
      <div className={styles.durationWrapper}>
        <div className={styles.competitorPrice}>
          <div className={styles.label}>Competitor price</div>
          <div className={styles.amount}>{formattedPricing && formattedPricing.msrp}</div>
        </div>
        <div className={styles.discount}>
          <div className={styles.label}>Discount</div>
          <div className={styles.amount}>{formattedPricing && formattedPricing.savings}</div>
        </div>
        <div className={styles.ourPrice}>
          <div className={styles.label}>Our price</div>
          <div className={styles.amount}>{formattedPricing && formattedPricing.price}</div>
        </div>
      </div>
    </div>
  )
}

interface DurationSelectorProps {
  sellable: DurationSelectorFragment
  onVariationSelected: (sellable: SellableUrlFragment) => void
}

type DurationType = 'hours' | 'days'

class DurationSelector extends React.Component<DurationSelectorProps, {}> {
  constructor(props: DurationSelectorProps) {
    super(props)

    this.handleDurationTypeChange = this.handleDurationTypeChange.bind(this)
  }

  public handleDurationTypeChange(durationType: DurationType) {
    const { sellable } = this.props

    if (sellable.variationMatrix) {
      if (durationType === 'hours') {
        // Find first variation that is less than a day
        const defaultHour = sellable.variationMatrix.dimensions[0].vectors.find(
          vector => parseInt(vector.value, 10) < 24
        )

        if (defaultHour) {
          const defaultHourSellable = sellable.variationMatrix.sellables.find(
            sellable => sellable.id === defaultHour.sellableIds[0]
          )

          defaultHourSellable && this.props.onVariationSelected(defaultHourSellable)
        }
      } else {
        const defaultDay = sellable.variationMatrix.dimensions[0].vectors.find(
          vector => parseInt(vector.value, 10) > 24
        )

        if (defaultDay) {
          const defaultDaySellable = sellable.variationMatrix.sellables.find(
            sellable => sellable.id === defaultDay.sellableIds[0]
          )

          defaultDaySellable && this.props.onVariationSelected(defaultDaySellable)
        }
      }
    }
  }

  render() {
    const { sellable, onVariationSelected } = this.props

    if (!sellable.variationMatrix || !sellable.variationMatrix.defaultVariant) {
      return null
    }

    const variations = sellable.variationMatrix
    const availableDimension = variations.dimensions && variations.dimensions[0]
    if (!availableDimension) {
      return null
    }

    const hourOptions = availableDimension.vectors
      .filter(vector => parseInt(vector.value, 10) < 24)
      .map(vector => ({ value: vector.sellableIds[0], label: vector.displayValueHtml }))

    const dayOptions = availableDimension.vectors
      .filter(vector => parseInt(vector.value, 10) > 24)
      .map(vector => ({
        value: vector.sellableIds[0],
        label: vector.displayValueHtml
      }))

    const charterType = parseInt(sellable.variants[0].value, 10) > 24 ? 'days' : 'hours'

    return (
      <div className={styles.duration}>
        <div className={styles.durationWrapper}>
          <div className={styles.durationTitle}>Book your charter</div>
          <div className={styles.durationSubtitle}>How long would you like to charter for?</div>
          <div className={styles.selectors}>
            <Select
              label="Pick your itinerary"
              onChange={e => this.handleDurationTypeChange(e.target.value as DurationType)}
              value={charterType}
              options={[
                { label: 'Per Hour', value: 'hours', disabled: !hourOptions.length },
                { label: 'Per Day', value: 'days', disabled: !dayOptions.length }
              ]}
            />
            <Select
              label="duration"
              value={sellable.id}
              labelHidden={true}
              options={charterType === 'hours' ? hourOptions : dayOptions}
              onChange={e => {
                // This is checked at the beginning of the render method
                const targetSellable = sellable.variationMatrix!.sellables.find(
                  sellable => sellable.id === e.target.value
                )

                targetSellable && onVariationSelected(targetSellable)
              }}
            />
          </div>
        </div>

        <DurationPrice sellable={sellable} />
      </div>
    )
  }
}

interface DurationProps {
  sellableId: string
  onVariationSelected: (sellable: SellableUrlFragment) => void
}

const Duration: React.SFC<DurationProps> = ({ sellableId, onVariationSelected }) => {
  return (
    <DurationSelectorComponent
      variables={{
        id: sellableId
      }}
    >
      {({ data, loading }) => {
        if (loading) {
          return <FullBlockLoader loader={CircleLoader} />
        } else if (data && data.sellable) {
          return (
            <DurationSelector sellable={data.sellable} onVariationSelected={onVariationSelected} />
          )
        }

        return null
      }}
    </DurationSelectorComponent>
  )
}

export default Duration
